import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function PrivacyPolicy() {
  const lastUpdated = "February 19, 2026";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/95 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Logo size="sm" />
            <Button variant="ghost" asChild>
              <Link to="/" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12">
        <article className="max-w-4xl mx-auto prose prose-slate dark:prose-invert">
          <h1 className="text-4xl font-bold text-foreground mb-2">PRIVACY POLICY — NORTH REPORTS</h1>
          <p className="text-muted-foreground mb-8">Last Updated: {lastUpdated}</p>

          <p className="text-foreground leading-relaxed">
            This Privacy Policy explains how North Reports LLC ("we," "our," or "us"), a Kentucky limited liability company, collects, uses, stores, and protects information when you use our platform, website, or related services ("Services").
          </p>

          <p className="text-foreground leading-relaxed">
            We build tools for real estate agents and teams to generate listing insights, market reports, and seller-ready content. To provide these Services effectively, we process certain types of data, including information connected through social media integrations.
          </p>

          <p className="text-foreground leading-relaxed">
            By using NORTH, you agree to the practices described below. This policy applies to all visitors, users, and others who access the Services.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">1. Information We Collect</h2>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.1 Information You Provide</h3>
          <ul className="text-foreground space-y-2">
            <li>Account details such as name, email address, team affiliation, and password.</li>
            <li>Listing information such as property details, photos, pricing, and notes.</li>
            <li>Inputs used for AI features, including Agent Insights and narrative generation.</li>
            <li>Communications with our support team.</li>
          </ul>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.2 Information Collected Automatically</h3>
          <ul className="text-foreground space-y-2">
            <li>Log data, device information, IP address, and usage activity.</li>
            <li>Browser type and version, operating system, and screen resolution.</li>
            <li>Referring URLs and pages visited within our Services.</li>
            <li>Cookies and similar technologies for performance and analytics.</li>
            <li>Cloudflare Turnstile tokens for bot protection (no personal data stored).</li>
          </ul>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.3 Payment Information</h3>
          <ul className="text-foreground space-y-2">
            <li>Billing details are processed securely by Stripe, our payment processor.</li>
            <li>We do not store your full credit card numbers on our servers.</li>
            <li>We retain transaction history for accounting and support purposes.</li>
          </ul>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.4 Social Media and Third-Party Integrations</h3>
          <p className="text-foreground leading-relaxed">
            If you connect Facebook, Instagram, YouTube, or other platforms through NORTH, we may receive:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Profile/page identifiers</li>
            <li>Post data (images, thumbnails, captions, dates, metrics)</li>
            <li>Engagement data (views, likes, comments, impressions)</li>
            <li>Token information required to maintain your authorized connection</li>
          </ul>
          <p className="text-foreground leading-relaxed font-semibold">
            We never store your social media passwords.
          </p>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.5 Gmail Integration</h3>
          <p className="text-foreground leading-relaxed">
            If you choose to connect your Google account for email functionality, we request access to:
          </p>
          <ul className="text-foreground space-y-2">
            <li><strong>Email address (userinfo.email):</strong> To identify your account and set the sender address for outgoing emails.</li>
            <li><strong>Send email (gmail.send):</strong> To send listing reports to your clients on your behalf, so recipients see the email coming from your real email address.</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            <strong>What we do not do:</strong>
          </p>
          <ul className="text-foreground space-y-2">
            <li>We do not read, scan, or access your inbox or existing emails.</li>
            <li>We do not store the content of emails sent through our platform.</li>
            <li>We do not share your Gmail data with any third parties.</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            You may revoke Gmail access at any time through your <a href="https://myaccount.google.com/permissions" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Google Account permissions</a> or by disconnecting your Google account in your North Reports settings.
          </p>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.6 MLS, Market, and Neighborhood Data</h3>
          <p className="text-foreground leading-relaxed">
            We may collect or process publicly available real estate and neighborhood metrics to generate insights and reports.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="text-foreground leading-relaxed">We use collected information to:</p>
          <ul className="text-foreground space-y-2">
            <li>Deliver and improve the NORTH platform</li>
            <li>Generate AI-powered summaries, insights, and reports</li>
            <li>Process payments and manage your subscription</li>
            <li>Sync connected social media content and analytics</li>
            <li>Create personalized listing and market experiences for your clients</li>
            <li>Authenticate your account and maintain security</li>
            <li>Provide customer support</li>
            <li>Send listing reports to your clients via Gmail on your behalf (with your explicit permission)</li>
            <li>Send transactional emails (receipts, subscription updates, security alerts)</li>
            <li>Analyze usage trends to improve product performance</li>
            <li>Enforce our Terms of Service and prevent fraud</li>
          </ul>
          <p className="text-foreground leading-relaxed font-semibold">
            We do not sell your personal information to third parties.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">3. How We Share Information</h2>
          <p className="text-foreground leading-relaxed">We may share information with:</p>
          <ul className="text-foreground space-y-2">
            <li><strong>Service providers:</strong> Hosting platforms (Supabase), payment processors (Stripe), analytics tools, and API partners</li>
            <li><strong>AI processing partners:</strong> Used to generate reports and insights</li>
            <li><strong>Security services:</strong> Cloudflare for DDoS protection and bot prevention</li>
            <li><strong>Your team:</strong> If your account is team-managed, other team members may see shared content</li>
            <li><strong>Legal authorities:</strong> If required by law, court order, or to protect our rights</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            We do not share your social media tokens except as required to deliver the Services. All service providers are bound by confidentiality agreements.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">4. Cookies and Tracking Technologies</h2>
          <p className="text-foreground leading-relaxed">
            We use cookies and similar technologies to operate and improve our Services:
          </p>
          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Essential Cookies</h3>
          <ul className="text-foreground space-y-2">
            <li><strong>Authentication:</strong> Session tokens to keep you logged in</li>
            <li><strong>Security:</strong> Cloudflare Turnstile for bot protection</li>
            <li><strong>Preferences:</strong> Remember your settings and choices</li>
          </ul>
          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Analytics Cookies</h3>
          <ul className="text-foreground space-y-2">
            <li>Usage patterns to improve the platform</li>
            <li>Error tracking to fix issues quickly</li>
            <li>Performance metrics to optimize loading times</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            Most browsers allow you to control cookies through settings. Blocking essential cookies may prevent the Services from functioning properly.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">5. Data Storage, Security & Retention</h2>
          <p className="text-foreground leading-relaxed">
            We use modern security measures to protect your information:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Data encryption in transit (TLS/SSL) and at rest</li>
            <li>Secure cloud infrastructure with access controls</li>
            <li>Regular security audits and monitoring</li>
            <li>Employee access limited to need-to-know basis</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            <strong>Data Retention:</strong> We retain your data as long as your account is active. After account deletion, we may retain certain data for up to 90 days for backup purposes, and longer if required for legal, accounting, or fraud prevention purposes.
          </p>
          <p className="text-foreground leading-relaxed">
            If you disconnect a social account, we remove associated tokens immediately.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">6. Your Privacy Rights</h2>
          <p className="text-foreground leading-relaxed">
            Depending on your location, you may have the following rights:
          </p>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">All Users</h3>
          <ul className="text-foreground space-y-2">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update inaccurate information</li>
            <li><strong>Deletion:</strong> Request removal of your data (subject to legal retention requirements)</li>
            <li><strong>Export:</strong> Download your data in a portable format</li>
            <li><strong>Withdraw Consent:</strong> Revoke permissions at any time</li>
          </ul>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">California Residents (CCPA)</h3>
          <p className="text-foreground leading-relaxed">
            Under the California Consumer Privacy Act (CCPA), you have additional rights:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Right to know what personal information we collect and why</li>
            <li>Right to delete personal information we have collected</li>
            <li>Right to opt-out of the sale of personal information (we do not sell your data)</li>
            <li>Right to non-discrimination for exercising your privacy rights</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            To exercise these rights, contact us at <a href="mailto:privacy@northreports.com" className="text-primary hover:underline">privacy@northreports.com</a>. We will respond within 45 days.
          </p>

          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">European Users (GDPR)</h3>
          <p className="text-foreground leading-relaxed">
            Under the General Data Protection Regulation (GDPR), you have additional rights:
          </p>
          <ul className="text-foreground space-y-2">
            <li><strong>Legal Basis:</strong> We process your data based on contract performance (to provide Services), legitimate interests (to improve our platform), and consent (for optional features)</li>
            <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
            <li><strong>Right to Object:</strong> Object to certain processing activities</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in machine-readable format</li>
            <li><strong>Right to Lodge Complaint:</strong> File a complaint with your local data protection authority</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            <strong>Data Transfers:</strong> Your data may be processed in the United States. We use appropriate safeguards for international transfers.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">7. Third-Party Services</h2>
          <p className="text-foreground leading-relaxed">
            Our Services integrate with third-party providers, each with their own privacy policies:
          </p>
          <ul className="text-foreground space-y-2">
            <li><strong>Stripe:</strong> Payment processing - <a href="https://stripe.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
            <li><strong>Supabase:</strong> Database and authentication - <a href="https://supabase.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a></li>
            <li><strong>Cloudflare:</strong> Security and performance - <a href="https://cloudflare.com/privacypolicy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">cloudflare.com/privacypolicy</a></li>
            <li><strong>Google Gmail API:</strong> Email sending on your behalf - <a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
            <li><strong>OpenAI/Anthropic:</strong> AI-generated content (no personal data shared)</li>
          </ul>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">8. Children&apos;s Privacy</h2>
          <p className="text-foreground leading-relaxed">
            NORTH is not intended for individuals under 18. We do not knowingly collect data from minors. If you believe we have collected information from someone under 18, please contact us immediately and we will delete it.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">9. Changes to This Policy</h2>
          <p className="text-foreground leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of material changes via email or through the platform. Updates are posted with a revised "Last Updated" date. Your continued use of the Services after changes constitutes acceptance of the updated policy.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">10. Contact Us</h2>
          <p className="text-foreground leading-relaxed">
            For privacy questions, data requests, or concerns:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Email: <a href="mailto:privacy@northreports.com" className="text-primary hover:underline">privacy@northreports.com</a></li>
            <li>General support: <a href="mailto:support@northreports.com" className="text-primary hover:underline">support@northreports.com</a></li>
          </ul>
          <p className="text-foreground leading-relaxed mt-4">
            North Reports LLC<br />
            Kentucky, United States
          </p>
          <p className="text-foreground leading-relaxed">
            We will respond to data requests within 30-45 days depending on your jurisdiction.
          </p>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card mt-12">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} North Reports. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
